import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  ArrowsClockwise, CheckCircle, Truck, WarningCircle, CaretDown, CaretRight, MagnifyingGlass, Wrench, Lock,
} from "@phosphor-icons/react";
import { http } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import {
  FILTERS, urgency, rankVehicles, summarize, filterVehicles,
  dueLabel, reminderDue, formatMeter, ageLabel, isStale, lampLabels, isStopLamp,
  faultKey, lookupBody, kbBadge, severityColor,
} from "@/lib/fleetHealth";

const U = {
  oos:       { color: "#ef4444", label: "OUT OF SERVICE" },
  critical:  { color: "#ef4444", label: "STOP LAMP" },
  overdue:   { color: "#f97316", label: "OVERDUE" },
  attention: { color: "#fbbf24", label: "ATTENTION" },
  ok:        { color: "#22c55e", label: "HEALTHY" },
};

export default function FleetHealthPage() {
  const { user } = useAuth();
  const canSync = user?.role === "admin" || user?.role === "super_admin";
  const [status, setStatus] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const st = (await http.get("/fleetio/status")).data;
      setStatus(st);
      if (st.is_connected) {
        const res = (await http.get("/fleetio/vehicles")).data;
        setVehicles(Array.isArray(res) ? res : res.vehicles || []);
      } else {
        setVehicles([]);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load Fleetio data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      await http.post("/fleetio/sync");
      toast.success("Fleetio sync complete");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const ranked = useMemo(() => rankVehicles(vehicles), [vehicles]);
  const rows = useMemo(() => filterVehicles(ranked, filter, query), [ranked, filter, query]);
  const sum = useMemo(() => summarize(vehicles), [vehicles]);
  const stale = status?.is_connected && isStale(status);

  return (
    <div className="space-y-6" data-testid="fleet-health-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#a1a1aa]">Fleetio · read-only</div>
          <h1 className="font-heading font-black text-3xl tracking-tight">Fleet Health</h1>
          {status?.is_connected && (
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717a] mt-1" data-testid="fh-sync-line">
              Last synced {ageLabel(status.last_success_at)} · refreshes every {Math.round((status.sync_interval_s || 900) / 60)} min
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canSync && status?.is_connected && (
            <button onClick={syncNow} disabled={syncing}
              className="px-3 py-1.5 border border-[#2a2a2a] hover:border-[#ff8c00] text-[10px] uppercase tracking-[0.25em] font-bold flex items-center gap-2"
              data-testid="fh-sync">
              <ArrowsClockwise size={11} weight="bold" className={syncing ? "animate-spin" : ""} /> Sync now
            </button>
          )}
          <button onClick={load} disabled={loading}
            className="px-3 py-1.5 border border-[#2a2a2a] hover:border-[#ff8c00] text-[10px] uppercase tracking-[0.25em] font-bold flex items-center gap-2"
            data-testid="fh-refresh">
            <ArrowsClockwise size={11} weight="bold" className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {error && <Banner color="#ef4444" testid="fh-error">{error}</Banner>}

      {!error && status && !status.is_connected && (
        <div className="bg-[#0d0d0d] border border-[#2a2a2a] px-4 py-14 text-center text-xs text-[#71717a] uppercase tracking-[0.25em]" data-testid="fh-not-connected">
          <Wrench size={28} className="mx-auto mb-2 text-[#ff8c00]" />
          Fleetio isn't connected for this company yet.
        </div>
      )}

      {status?.is_connected && (
        <>
          {status.last_error && (
            <Banner color="#ef4444" testid="fh-sync-error">Last Fleetio sync failed: {String(status.last_error).slice(0, 200)}</Banner>
          )}
          {stale && !status.last_error && (
            <Banner color="#fbbf24" testid="fh-stale">
              Data may be out of date. Last successful sync: {ageLabel(status.last_success_at)}.
            </Banner>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Tile label="Units" value={sum.units} accent="#a1a1aa" testid="fh-tile-units" />
            <Tile label="Out of service" value={sum.outOfService} accent="#ef4444" testid="fh-tile-oos" />
            <Tile label="Overdue units" value={sum.overdueUnits} accent="#f97316" testid="fh-tile-overdue" />
            <Tile label="Active faults" value={sum.faultUnits} accent="#fbbf24" testid="fh-tile-faults" />
            <Tile label="Healthy" value={sum.healthy} accent="#22c55e" testid="fh-tile-healthy" />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {FILTERS.map((f) => (
              <button key={f.id} onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 border text-[10px] uppercase tracking-[0.25em] font-bold ${filter === f.id ? "border-[#ff8c00] text-[#ff8c00]" : "border-[#2a2a2a] text-[#a1a1aa] hover:border-[#ff8c00]"}`}
                data-testid={`fh-filter-${f.id}`}>
                {f.label}
              </button>
            ))}
            <div className="relative ml-auto">
              <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#71717a]" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search unit, VIN, type"
                className="pl-7 pr-3 py-1.5 bg-[#1a1a1a] border border-[#2a2a2a] text-xs w-56 max-w-full" data-testid="fh-search" />
            </div>
          </div>

          <div className="bg-[#0d0d0d] border border-[#2a2a2a]" data-testid="fh-list">
            <div className="px-4 py-3 border-b border-[#2a2a2a] flex items-center justify-between">
              <h2 className="font-heading font-bold text-sm tracking-tight uppercase">Units by urgency</h2>
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717a]">{rows.length} of {vehicles.length}</span>
            </div>
            {rows.length === 0 ? (
              <div className="px-4 py-14 text-center text-xs text-[#71717a] uppercase tracking-[0.25em]" data-testid="fh-empty">
                <CheckCircle size={28} weight="fill" className="text-[#22c55e] mx-auto mb-2" />
                {vehicles.length === 0 ? "No Fleetio vehicles synced yet." : "No units match this view."}
              </div>
            ) : (
              <div className="divide-y divide-[#2a2a2a]">
                {rows.map((v) => (
                  <VehicleRow key={v.fleetio_id} v={v} expanded={!!open[v.fleetio_id]}
                    onToggle={() => setOpen((o) => ({ ...o, [v.fleetio_id]: !o[v.fleetio_id] }))} />
                ))}
              </div>
            )}
          </div>

          {sum.unmatched > 0 && (
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717a] flex items-center gap-2" data-testid="fh-unmatched">
              <Lock size={11} /> {sum.unmatched} Fleetio unit{sum.unmatched === 1 ? "" : "s"} not matched to a LoadLogic asset (matched by VIN, then name).
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Fetch what the DieselOps knowledge base says about a unit's faults, once per expand. */
function useFaultLookup(faults, enabled) {
  const [kb, setKb] = useState(null);
  const sig = (faults || []).map(faultKey).filter(Boolean).join("|");
  useEffect(() => {
    if (!enabled || !sig) return;
    let alive = true;
    setKb({ loading: true });
    http.post("/fleetio/fault-lookup", lookupBody(faults))
      .then((r) => { if (alive) setKb(r.data); })
      .catch(() => { if (alive) setKb({ available: false, reason: "error", results: {} }); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, sig]);
  return kb;
}

function FaultInfo({ info }) {
  if (!info) return null;
  const b = kbBadge(info.status);
  const m = info.matches || [];
  return (
    <div className="mt-1 ml-3 pl-3 border-l border-[#2a2a2a] space-y-1" data-testid="fh-kb-info">
      <span className="text-[9px] uppercase tracking-[0.2em] font-black" style={{ color: b.color }}>{b.label}</span>
      {info.status === "kb_match" && m.map((e, i) => (
        <div key={i} className="text-[#d4d4d8]">
          <div>
            {e.short_description}
            {e.severity && <span className="font-bold" style={{ color: severityColor(e.severity) }}> · {String(e.severity).toUpperCase()}</span>}
            {e.engine_family && <span className="text-[#71717a]"> · {e.engine_family}</span>}
            {e.verified_by_mechanic && <span className="text-[#22c55e]"> · verified by mechanic</span>}
          </div>
          {e.likely_causes?.length > 0 && <div className="text-[#a1a1aa]">Likely causes: {e.likely_causes.join("; ")}</div>}
          {e.first_diagnostic_step && <div className="text-[#a1a1aa]">Check first: {e.first_diagnostic_step}</div>}
          {e.warnings && <div className="text-[#fbbf24]">Note: {e.warnings}</div>}
        </div>
      ))}
      {info.status === "spn_related" && (
        <div className="text-[#a1a1aa]">
          Same SPN with a different FMI is in the knowledge base. Related, not the same fault:
          {m.map((e, i) => <span key={i} className="text-[#d4d4d8]"> {e.short_description}{e.fmi != null ? ` (FMI ${e.fmi})` : ""}{i < m.length - 1 ? ";" : ""}</span>)}
        </div>
      )}
      {info.status !== "kb_match" && info.status !== "spn_related" && (
        <div className="text-[#71717a]">Not in the DieselOps knowledge base yet. Use the Samsara description above.</div>
      )}
    </div>
  );
}

function VehicleRow({ v, expanded, onToggle }) {
  const kb = useFaultLookup(v.active_faults, expanded);
  const u = urgency(v);
  const meta = U[u];
  const overdueN = (v.overdue_reminders?.length || 0) + (v.overdue_issue_count || 0);
  const Caret = expanded ? CaretDown : CaretRight;
  return (
    <div data-testid={`fh-row-${v.fleetio_id}`}>
      <button onClick={onToggle} className="w-full px-4 py-3 flex items-center gap-3 flex-wrap text-left hover:bg-[#141414]" aria-expanded={expanded}>
        <span className="text-[9px] uppercase tracking-[0.25em] font-black px-1.5 py-0.5 shrink-0" style={{ background: meta.color, color: "#000" }}>
          {meta.label}
        </span>
        <div className="w-7 h-7 bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center shrink-0">
          <Truck size={11} className="text-[#ff8c00]" weight="bold" />
        </div>
        <div className="flex-1 min-w-[9rem]">
          <div className="flex items-center gap-2 flex-wrap text-sm">
            <span className="font-mono font-bold text-[#ff8c00]">{v.name}</span>
            {v.type && <span className="text-[10px] text-[#71717a]">{v.type}</span>}
            {v.status_name && <span className="text-[10px] text-[#a1a1aa]">· {v.status_name}</span>}
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717a] font-mono mt-0.5">
            {[formatMeter(v.meter), v.vin].filter(Boolean).join(" · ")}
          </div>
        </div>
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] w-full sm:w-auto justify-end sm:shrink-0">
          {overdueN > 0 && <span style={{ color: "#f97316" }}>{overdueN} overdue</span>}
                    {(v.active_faults?.length || 0) > 0 && <span style={{ color: "#fbbf24" }}>{v.active_faults.length} fault{v.active_faults.length === 1 ? "" : "s"}</span>}
          {(v.open_issues?.length || 0) > 0 && <span style={{ color: "#fbbf24" }}>{v.open_issues.length} issue{v.open_issues.length === 1 ? "" : "s"}</span>}
          {(v.due_soon_reminders?.length || 0) > 0 && <span style={{ color: "#4682b4" }}>{v.due_soon_reminders.length} due soon</span>}
          <Caret size={12} className="text-[#71717a]" />
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-4 pl-14 space-y-3 text-sm" data-testid={`fh-detail-${v.fleetio_id}`}>
          {v.out_of_service && (
            <div className="text-xs" style={{ color: "#ef4444" }}>
              <WarningCircle size={12} className="inline mr-1" weight="fill" />
              Out of service{v.out_of_service_reasons?.length ? `: ${v.out_of_service_reasons.join(", ")}` : ""}
            </div>
          )}
          <Section title={`Active fault codes${lampLabels(v.check_engine).length ? " · lamps: " + lampLabels(v.check_engine).join(", ") : ""}`} items={v.active_faults}>
            {(f) => (
              <>
                <span className="font-mono font-bold text-white">{f.code}</span>
                {f.description && <span className="text-[#a1a1aa]"> · {f.description}</span>}
                <span className="text-[#71717a]">{f.source ? ` · ${f.source}` : ""}{f.count != null ? ` · seen ${f.count}×` : ""}</span>
                {kb?.available && <FaultInfo info={kb.results?.[faultKey(f)]} />}
              </>
            )}
          </Section>
          {(v.active_faults?.length || 0) > 0 && (
            <div className="text-[10px] text-[#71717a]" data-testid="fh-kb-footer">
              {kb?.loading && "Checking the DieselOps knowledge base…"}
              {kb?.available && (kb.disclaimer || "Reference information, not a diagnosis. A technician must confirm any fault on the vehicle.")}
              {kb && !kb.loading && !kb.available && (kb.reason === "not_configured"
                ? "DieselOps knowledge base not connected (set DIESELOPS_SERVICE_TOKEN)."
                : "DieselOps knowledge base unavailable right now. Showing Samsara's descriptions only.")}
            </div>
          )}
          <Section title="Open issues" items={v.open_issues}>
            {(i) => (
              <>
                <span className="font-mono text-[#a1a1aa]">#{i.number}</span> {i.name}
                <span className="text-[#71717a]"> · {i.state}{i.priority ? ` · ${i.priority}` : ""}</span>
                {i.overdue && <span style={{ color: "#f97316" }}> · {dueLabel(i.due_date).text || "overdue"}</span>}
              </>
            )}
          </Section>
          <Section title="Overdue service reminders" items={v.overdue_reminders}>
            {(r) => <ReminderLine r={r} />}
          </Section>
          <Section title="Due soon" items={v.due_soon_reminders}>
            {(r) => <ReminderLine r={r} />}
          </Section>
          {u === "ok" && <div className="text-xs text-[#71717a]">Nothing open and no active fault codes.</div>}
        </div>
      )}
    </div>
  );
}

function ReminderLine({ r }) {
  const d = reminderDue(r);
  return (
    <>
      {r.task}
      <span className="text-[#71717a]"> · {d.when}</span>
      {d.relative && <span style={{ color: d.overdue ? "#f97316" : "#4682b4" }}> · {d.relative}</span>}
    </>
  );
}

function Section({ title, items, children }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#a1a1aa] mb-1">{title}</div>
      <ul className="space-y-1">
        {items.map((it, idx) => (
          <li key={it.fleetio_id ?? idx} className="text-xs">{children(it)}</li>
        ))}
      </ul>
    </div>
  );
}

function Banner({ color, testid, children }) {
  return (
    <div className="px-4 py-2 border text-xs" style={{ borderColor: color, color }} data-testid={testid}>{children}</div>
  );
}

function Tile({ label, value, accent, testid }) {
  return (
    <div className="bg-[#0d0d0d] border border-[#2a2a2a] p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.25em]" style={{ color: accent }}>{label}</div>
      <div className="text-2xl font-heading font-black mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}
